import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchTrainerCourses,
  deleteTrainerCourse,
  generateCourseActivationCodes,
} from "@/api/trainerAuth";

export default function TrainerCourses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [generatingCodes, setGeneratingCodes] = useState(null);

  const navigate = useNavigate();

  const loadCourses = async () => {
    try {
      setLoading(true);
      const data = await fetchTrainerCourses();
      setCourses(data);
    } catch (e) {
      console.error(e);
      setError("فشل تحميل الكورسات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCourses();
  }, []);

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm("هل أنت متأكد من حذف هذا الكورس؟");
    if (!confirmDelete) return;

    try {
      await deleteTrainerCourse(id);
      setCourses((prev) => prev.filter((c) => c.id !== id));
    } catch (e) {
      console.error(e);
      alert("فشل حذف الكورس");
    }
  };

  const handleGenerateCodes = async (courseId, courseTitle) => {
    const count = prompt(`كم عدد أكواد التفعيل التي تريد إنشائها لكورس "${courseTitle}"؟`, "10");
    if (!count || isNaN(count) || parseInt(count) <= 0) return;

    setGeneratingCodes(courseId);
    try {
      const res = await generateCourseActivationCodes(courseId, Number(count));
      alert(`✅ تم توليد ${res.codes?.length || count} كود تفعيل بنجاح لكورس "${courseTitle}"`);
    } catch (e) {
      console.error(e);
      alert("❌ فشل توليد أكواد التفعيل");
    } finally {
      setGeneratingCodes(null);
    }
  };

  // الحصول على الفئات الفريدة للتصفية
  const categories = [...new Set(courses.map(course => course.category?.name).filter(Boolean))];

  // تصفية الكورسات
  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "all" || course.category?.name === selectedCategory;
    const matchesStatus = selectedStatus === "all" || 
                         (selectedStatus === "active" && course.is_active) ||
                         (selectedStatus === "inactive" && !course.is_active);
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // عرض حالة التحميل
  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>جاري تحميل الكورسات...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.pageTitle}>كورساتي</h1>
        <p style={styles.pageSubtitle}>إدارة جميع الكورسات الخاصة بك</p>
      </div>

      <div style={styles.content}>
        {error && (
          <div style={styles.errorMessage}>
            <span style={styles.errorIcon}>⚠️</span>
            {error}
          </div>
        )}

        {/* بطاقة الإحصائيات */}
        <div style={styles.statsCard}>
          <div style={styles.statItem}>
            <span style={styles.statIcon}>📚</span>
            <div>
              <h3 style={styles.statNumber}>{courses.length}</h3>
              <p style={styles.statLabel}>إجمالي الكورسات</p>
            </div>
          </div>
          <div style={styles.statItem}>
            <span style={styles.statIcon}>✅</span>
            <div>
              <h3 style={styles.statNumber}>{courses.filter(c => c.is_active).length}</h3>
              <p style={styles.statLabel}>كورسات مفعلة</p>
            </div>
          </div>
          <div style={styles.statItem}>
            <span style={styles.statIcon}>👨‍🎓</span>
            <div>
              <h3 style={styles.statNumber}>
                {courses.reduce((sum, course) => sum + (course.students_count || 0), 0)}
              </h3>
              <p style={styles.statLabel}>إجمالي الطلاب</p>
            </div>
          </div>
          <div style={styles.statItem}>
            <span style={styles.statIcon}>💰</span>
            <div>
              <h3 style={styles.statNumber}>
                {courses.reduce((sum, course) => sum + (course.revenue || 0), 0)} ر.س
              </h3>
              <p style={styles.statLabel}>إجمالي الإيرادات</p>
            </div>
          </div>
        </div>

        {/* أدوات التصفية والبحث */}
        <div style={styles.filterCard}>
          <div style={styles.filterGrid}>
            <div style={styles.searchBox}>
              <input
                type="text"
                placeholder="🔍 ابحث عن كورس..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
              />
            </div>
            
            <select 
              value={selectedCategory} 
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="all">جميع الفئات</option>
              {categories.map((cat, index) => (
                <option key={index} value={cat}>{cat}</option>
              ))}
            </select>
            
            <select 
              value={selectedStatus} 
              onChange={(e) => setSelectedStatus(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="all">جميع الحالات</option>
              <option value="active">مفعل</option>
              <option value="inactive">غير مفعل</option>
            </select>
            
            <button 
              style={styles.createButton}
              onClick={() => navigate("/trainer/courses/create")}
            >
              + إنشاء كورس جديد
            </button>
          </div>
        </div>

        {/* جدول الكورسات */}
        <div style={styles.tableCard}>
          {filteredCourses.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>📚</div>
              <h3 style={styles.emptyTitle}>
                {courses.length === 0 ? "لا يوجد كورسات بعد" : "لا توجد نتائج بحث"}
              </h3>
              <p style={styles.emptyText}>
                {courses.length === 0 
                  ? "ابدأ بإنشاء أول كورس لك" 
                  : "جرب مصطلحات بحث أو تصفية مختلفة"}
              </p>
              {courses.length === 0 && (
                <button 
                  style={styles.createEmptyButton}
                  onClick={() => navigate("/trainer/courses/create")}
                >
                  إنشاء كورس جديد
                </button>
              )}
            </div>
          ) : (
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>#</th>
                    <th style={styles.th}>الكورس</th>
                    <th style={styles.th}>الفئة</th>
                    <th style={styles.th}>المستوى</th>
                    <th style={styles.th}>السعر</th>
                    <th style={styles.th}>الطلاب</th>
                    <th style={styles.th}>الحالة</th>
                    <th style={styles.th}>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCourses.map((course, index) => (
                    <tr key={course.id} style={styles.tr}>
                      <td style={styles.td}>{index + 1}</td>
                      <td style={styles.td}>
                        <div style={styles.courseCell}>
                          {course.thumbnail_url ? (
                            <img 
                              src={course.thumbnail_url} 
                              alt={course.title}
                              style={styles.courseImage}
                            />
                          ) : (
                            <div style={styles.courseImagePlaceholder}>📚</div>
                          )}
                          <div style={styles.courseInfo}>
                            <h4 style={styles.courseTitle}>{course.title}</h4>
                            <p style={styles.courseDesc}>
                              {course.short_description?.substring(0, 60)}...
                            </p>
                            <small style={styles.courseDate}>
                              {new Date(course.created_at).toLocaleDateString('ar-SA')}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.categoryBadge}>
                          {course.category?.name || "غير محدد"}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.levelBadge,
                          ...(course.level === 'beginner' && styles.levelBeginner),
                          ...(course.level === 'intermediate' && styles.levelIntermediate),
                          ...(course.level === 'advanced' && styles.levelAdvanced),
                        }}>
                          {course.level === 'beginner' ? 'مبتدئ' :
                           course.level === 'intermediate' ? 'متوسط' :
                           course.level === 'advanced' ? 'متقدم' : course.level}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <strong style={styles.priceText}>
                          {course.price == 0 ? 'مجاني' : `${course.price} ر.س`}
                        </strong>
                      </td>
                      <td style={styles.td}>
                        <div style={styles.studentsCell}>
                          <span style={styles.studentsCount}>{course.students_count || 0}</span>
                          <span style={styles.studentsLabel}>طالب</span>
                        </div>
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.statusBadge,
                          ...(course.is_active ? styles.statusActive : styles.statusInactive)
                        }}>
                          {course.is_active ? "✅ مفعل" : "❌ غير مفعل"}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <div style={styles.actionsContainer}>
                          <button
                            style={styles.actionButtonContent}
                            onClick={() => navigate(`/trainer/courses/${course.id}/content`)}
                            title="إدارة المحتوى"
                          >
                            📝
                          </button>
                          
                          <button
                            style={styles.actionButtonAI}
                            onClick={() => navigate(`/trainer/courses/${course.id}/content?ai=true`)}
                            title="توليد السكشنات بالذكاء الاصطناعي"
                          >
                            🤖
                          </button>
                          
                          <button
                            style={styles.actionButtonCodes}
                            onClick={() => handleGenerateCodes(course.id, course.title)}
                            disabled={generatingCodes === course.id}
                            title="توليد أكواد تفعيل"
                          >
                            {generatingCodes === course.id ? (
                              <span style={styles.smallSpinner}></span>
                            ) : (
                              "🔑"
                            )}
                          </button>
                          
                          <button
                            style={styles.actionButtonDelete}
                            onClick={() => handleDelete(course.id)}
                            title="حذف الكورس"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* تذييل الصفحة */}
        <div style={styles.footer}>
          <div style={styles.pagination}>
            <button style={styles.pageButton} disabled>‹ السابق</button>
            <span style={styles.pageInfo}>الصفحة 1 من 1</span>
            <button style={styles.pageButton} disabled>التالي ›</button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    direction: 'rtl',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    background: '#f5f7fa',
    minHeight: '100vh',
  },
  header: {
    background: 'linear-gradient(135deg, #2c3e50 0%, #1a2530 100%)',
    color: 'white',
    padding: '30px',
    borderRadius: '0 0 15px 15px',
    marginBottom: '30px',
  },
  pageTitle: {
    fontSize: '2rem',
    margin: '0 0 10px 0',
    fontWeight: '700',
  },
  pageSubtitle: {
    fontSize: '1rem',
    opacity: '0.9',
    margin: 0,
  },
  content: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '0 20px 40px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '300px',
    color: '#666',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #f3f3f3',
    borderTop: '5px solid #667eea',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '20px',
  },
  errorMessage: {
    background: '#ffebee',
    color: '#c62828',
    padding: '15px 20px',
    borderRadius: '8px',
    marginBottom: '25px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    borderRight: '4px solid #f44336',
    animation: 'fadeIn 0.3s ease-out',
  },
  errorIcon: {
    fontSize: '1.5rem',
  },
  statsCard: {
    background: 'white',
    borderRadius: '15px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
    padding: '25px',
    marginBottom: '30px',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    padding: '15px',
    background: '#f8f9fa',
    borderRadius: '10px',
    transition: 'transform 0.3s ease',
  },
  statIcon: {
    fontSize: '2rem',
    width: '60px',
    height: '60px',
    background: 'linear-gradient(135deg, #667eea20 0%, #764ba220 100%)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#667eea',
  },
  statNumber: {
    margin: '0 0 5px 0',
    fontSize: '1.8rem',
    color: '#333',
    fontWeight: '700',
  },
  statLabel: {
    margin: 0,
    fontSize: '0.9rem',
    color: '#666',
  },
  filterCard: {
    background: 'white',
    borderRadius: '15px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
    padding: '25px',
    marginBottom: '30px',
  },
  filterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
    alignItems: 'center',
  },
  searchBox: {
    gridColumn: 'span 2',
  },
  searchInput: {
    width: '100%',
    padding: '12px 20px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
  },
  filterSelect: {
    padding: '12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    backgroundColor: 'white',
    fontFamily: 'inherit',
    cursor: 'pointer',
  },
  createButton: {
    padding: '12px 25px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  tableCard: {
    background: 'white',
    borderRadius: '15px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
    padding: '25px',
    marginBottom: '30px',
    animation: 'fadeIn 0.5s ease-out',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#777',
  },
  emptyIcon: {
    fontSize: '4rem',
    opacity: '0.5',
    marginBottom: '20px',
  },
  emptyTitle: {
    fontSize: '1.5rem',
    margin: '0 0 10px 0',
    color: '#555',
  },
  emptyText: {
    fontSize: '1rem',
    opacity: '0.8',
    marginBottom: '25px',
  },
  createEmptyButton: {
    padding: '12px 30px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  tableContainer: {
    overflowX: 'auto',
    borderRadius: '8px',
    border: '1px solid #f0f0f0',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '1000px',
  },
  th: {
    padding: '15px',
    textAlign: 'right',
    background: '#f8f9fa',
    color: '#555',
    fontWeight: '600',
    fontSize: '0.9rem',
    borderBottom: '2px solid #e0e0e0',
  },
  tr: {
    borderBottom: '1px solid #f0f0f0',
    transition: 'background 0.3s ease',
  },
  td: {
    padding: '15px',
    textAlign: 'right',
    fontSize: '0.9rem',
    color: '#333',
    verticalAlign: 'middle',
  },
  courseCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
  },
  courseImage: {
    width: '80px',
    height: '60px',
    borderRadius: '8px',
    objectFit: 'cover',
    flexShrink: 0,
  },
  courseImagePlaceholder: {
    width: '80px',
    height: '60px',
    borderRadius: '8px',
    background: '#f5f7fa',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.5rem',
    color: '#999',
    flexShrink: 0,
  },
  courseInfo: {
    flex: 1,
  },
  courseTitle: {
    margin: '0 0 5px 0',
    fontSize: '1rem',
    color: '#333',
    fontWeight: '600',
  },
  courseDesc: {
    margin: '0 0 5px 0',
    fontSize: '0.85rem',
    color: '#666',
    lineHeight: '1.4',
  },
  courseDate: {
    fontSize: '0.8rem',
    color: '#999',
  },
  categoryBadge: {
    padding: '5px 12px',
    background: '#e3f2fd',
    color: '#1976d2',
    borderRadius: '20px',
    fontSize: '0.8rem',
    fontWeight: '500',
  },
  levelBadge: {
    padding: '5px 12px',
    borderRadius: '20px',
    fontSize: '0.8rem',
    fontWeight: '500',
    display: 'inline-block',
  },
  levelBeginner: {
    background: '#e8f5e9',
    color: '#2e7d32',
  },
  levelIntermediate: {
    background: '#fff3e0',
    color: '#ef6c00',
  },
  levelAdvanced: {
    background: '#ffebee',
    color: '#c62828',
  },
  priceText: {
    color: '#333',
    fontWeight: '600',
  },
  studentsCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '5px',
  },
  studentsCount: {
    fontSize: '1.2rem',
    fontWeight: '700',
    color: '#667eea',
  },
  studentsLabel: {
    fontSize: '0.8rem',
    color: '#666',
  },
  statusBadge: {
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '0.8rem',
    fontWeight: '500',
    display: 'inline-block',
  },
  statusActive: {
    background: '#e8f5e9',
    color: '#2e7d32',
  },
  statusInactive: {
    background: '#ffebee',
    color: '#c62828',
  },
  actionsContainer: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
  },
  actionButtonContent: {
    width: '40px',
    height: '40px',
    background: '#e3f2fd',
    color: '#1976d2',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
  },
  actionButtonAI: {
    width: '40px',
    height: '40px',
    background: '#e8f5e9',
    color: '#2e7d32',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
  },
  actionButtonCodes: {
    width: '40px',
    height: '40px',
    background: '#fff3e0',
    color: '#ef6c00',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
  },
  actionButtonDelete: {
    width: '40px',
    height: '40px',
    background: '#ffebee',
    color: '#c62828',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
  },
  smallSpinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(239, 108, 0, 0.3)',
    borderTop: '2px solid #ef6c00',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  footer: {
    textAlign: 'center',
    padding: '20px',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '15px',
  },
  pageButton: {
    padding: '8px 16px',
    background: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    color: '#333',
    transition: 'all 0.3s ease',
  },
  pageInfo: {
    fontSize: '0.9rem',
    color: '#666',
  },
};

// إضافة تأثيرات Hover
if (typeof window !== 'undefined') {
  setTimeout(() => {
    const style = document.createElement('style');
    style.textContent = `
      .input-focus:focus, .select-focus:focus {
        outline: none;
        border-color: #667eea !important;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
      }
      
      .create-btn-hover:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3) !important;
      }
      
      .stat-item-hover:hover {
        transform: translateY(-5px) !important;
        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1) !important;
      }
      
      .table-row-hover:hover {
        background: #f8f9ff !important;
      }
      
      .action-btn-hover:hover {
        transform: scale(1.1) !important;
      }
      
      .page-btn-hover:hover:not(:disabled) {
        background: #667eea !important;
        color: white !important;
        border-color: #667eea !important;
      }
      
      .create-empty-btn-hover:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3) !important;
      }
      
      @media (max-width: 768px) {
        .filter-grid-responsive {
          grid-template-columns: 1fr !important;
          gap: 10px !important;
        }
        
        .search-box-responsive {
          grid-column: 1 !important;
        }
        
        .stats-card-responsive {
          grid-template-columns: repeat(2, 1fr) !important;
        }
      }
      
      @media (max-width: 480px) {
        .stats-card-responsive {
          grid-template-columns: 1fr !important;
        }
        
        .actions-container-responsive {
          flex-wrap: wrap !important;
          justify-content: center !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    // إضافة classes للعناصر
    setTimeout(() => {
      document.querySelectorAll('input').forEach(el => {
        el.className = (el.className || '') + ' input-focus';
      });
      document.querySelectorAll('select').forEach(el => {
        el.className = (el.className || '') + ' select-focus';
      });
      document.querySelectorAll('[style*="createButton"]').forEach(el => {
        el.className = (el.className || '') + ' create-btn-hover';
      });
      document.querySelectorAll('[style*="createEmptyButton"]').forEach(el => {
        el.className = (el.className || '') + ' create-empty-btn-hover';
      });
      document.querySelectorAll('[style*="statItem"]').forEach(el => {
        el.className = (el.className || '') + ' stat-item-hover';
      });
      document.querySelectorAll('[style*="tr"]').forEach(el => {
        el.className = (el.className || '') + ' table-row-hover';
      });
      document.querySelectorAll('[style*="actionButtonContent"]').forEach(el => {
        el.className = (el.className || '') + ' action-btn-hover';
      });
      document.querySelectorAll('[style*="actionButtonAI"]').forEach(el => {
        el.className = (el.className || '') + ' action-btn-hover';
      });
      document.querySelectorAll('[style*="actionButtonCodes"]').forEach(el => {
        el.className = (el.className || '') + ' action-btn-hover';
      });
      document.querySelectorAll('[style*="actionButtonDelete"]').forEach(el => {
        el.className = (el.className || '') + ' action-btn-hover';
      });
      document.querySelectorAll('[style*="pageButton"]').forEach(el => {
        el.className = (el.className || '') + ' page-btn-hover';
      });
      
      // Responsive classes
      if (window.innerWidth <= 768) {
        const filterGrid = document.querySelector('[style*="filterGrid"]');
        const searchBox = document.querySelector('[style*="searchBox"]');
        const statsCard = document.querySelector('[style*="statsCard"]');
        if (filterGrid) filterGrid.className = (filterGrid.className || '') + ' filter-grid-responsive';
        if (searchBox) searchBox.className = (searchBox.className || '') + ' search-box-responsive';
        if (statsCard) statsCard.className = (statsCard.className || '') + ' stats-card-responsive';
      }
      if (window.innerWidth <= 480) {
        const actionsContainers = document.querySelectorAll('[style*="actionsContainer"]');
        actionsContainers.forEach(el => {
          el.className = (el.className || '') + ' actions-container-responsive';
        });
      }
    }, 200);
  }, 100);
}